<template>
    <div class="d-flex">
        <b-sidebar
            id="sidebar-modal-periods"
            class="sidebar-modal-periods"
            right
            bg-variant="white"
            v-model="isOpen"
            :aria-hidden="false"
            @hidden="closeModal"
        >
            <template #header>
                <div class="header-sidebar d-flex justify-content-between">
                    <span class="sidebar-title">
                        {{
                            calendarSettings.modalPeriods.periodId === null
                                ? $t("CalendarsPage.NewPeriod")
                                : $t("CalendarsPage.EditPeriod")
                        }}
                    </span>

                    <span
                        class="close-icon"
                        @click="closeModal()"
                    >
                        <CloseIcon />
                    </span>
                </div>
                <div class="separator"></div>
            </template>

            <template>
                <div class="sidebar-modal-periods__fields">
                    <div class="sidebar-modal-periods__fields-row">
                        <div class="sidebar-modal-periods__day-field day-select">
                            <sys-form-select
                                :label="$t('CalendarsPage.Day')"
                                :placeholder="$t('Select')"
                                :value="calendarSettings.modalPeriods.fields.day"
                                :options="context.days"
                                :state="calendarSettings.modalPeriods.states.day.valid"
                                :feedback="$t(calendarSettings.modalPeriods.states.day.feedback)"
                                @input="(value) => UPDATE_MODAL_PERIODS_DAY_FIELD(value)"
                                translate
                            />
                        </div>
                    </div>
                    <div class="sidebar-modal-periods__fields-row">
                        <div class="sidebar-modal-periods__status-field">
                            <sys-form-select
                                :label="$t('ProductionList.Status')"
                                :placeholder="$t('Select')"
                                :value="calendarSettings.modalPeriods.fields.status"
                                :options="context.status"
                                :state="calendarSettings.modalPeriods.states.status.valid"
                                :feedback="$t(calendarSettings.modalPeriods.states.status.feedback)"
                                @input="(value) => handleSelectStatus(value)"
                                translate
                            />
                        </div>

                        <div class="sidebar-modal-periods__efficiency-field">
                            <sys-form-input
                                :label="$t('CalendarsPage.Efficiency')"
                                :value="calendarSettings.modalPeriods.fields.efficiency"
                                :placeholder="$t('TypeHere')"
                                :state="calendarSettings.modalPeriods.states.efficiency.valid"
                                :feedback="
                                    $t(calendarSettings.modalPeriods.states.efficiency.feedback)
                                "
                                @input="(e) => handleInputEfficiency(e.target.value)"
                                @blur="handleBlurEfficiency()"
                                @keypress="(e) => handleKeyPressEfficiency(e)"
                                :disabled="calendarSettings.modalPeriods.fields.status === null"
                            />
                        </div>
                    </div>

                    <div class="sidebar-modal-periods__fields-row">
                        <div class="sidebar-modal-periods__hour-fields">
                            <div class="sidebar-modal-periods__starts-field">
                                <sys-form-datepicker
                                    id="from-hour"
                                    type="hour"
                                    :label="$t('CalendarsPage.From')"
                                    :value="calendarSettings.modalPeriods.fields.startTime"
                                    :state="calendarSettings.modalPeriods.states.startTime.valid"
                                    :feedback="
                                        $t(calendarSettings.modalPeriods.states.startTime.feedback)
                                    "
                                    @input="(value) => UPDATE_MODAL_PERIODS_START_TIME_FIELD(value)"
                                />
                            </div>
                        </div>
                        <div
                            :class="[
                                'radio-group',
                                calendarSettings.general.fields.duration <=
                                calendarSettings.modalPeriods.fields.day
                                    ? '--disabled'
                                    : ''
                            ]"
                        >
                            <sys-radio-button
                                :label="$t('CalendarsPage.EndPeriodNextDay')"
                                :value="!calendarSettings.modalPeriods.fields.isSameDay"
                                :disabled="
                                    calendarSettings.general.fields.duration <=
                                    calendarSettings.modalPeriods.fields.day
                                "
                                @input="
                                    () => {
                                        clearFieldsOnChange();
                                        UPDATE_MODAL_PERIODS_IS_SAME_DAY_FIELD(false);
                                    }
                                "
                            />
                            <div
                                v-if="!calendarSettings.modalPeriods.fields.isSameDay"
                                class="sidebar-modal-periods__fields-row"
                            >
                                <div class="sidebar-modal-periods__hour-fields">
                                    <div class="sidebar-modal-periods__starts-field">
                                        <sys-form-datepicker
                                            id="from-hour--next-day"
                                            type="hour"
                                            :label="`${$t('CalendarsPage.From')} (${$t('ordersList.Day')} ${calendarSettings.modalPeriods.fields.day})`"
                                            :value="calendarSettings.modalPeriods.fields.startTime"
                                            :state="
                                                calendarSettings.modalPeriods.states.startTime.valid
                                            "
                                            :feedback="
                                                $t(
                                                    calendarSettings.modalPeriods.states.startTime
                                                        .feedback
                                                )
                                            "
                                            @input="
                                                (value) =>
                                                    UPDATE_MODAL_PERIODS_START_TIME_FIELD(value)
                                            "
                                        />
                                    </div>
                                </div>

                                <div class="sidebar-modal-periods__ends-field">
                                    <sys-form-datepicker
                                        id="to-hour-next-day"
                                        type="hour"
                                        :label="`${$t('CalendarsPage.To')} (${$t('ordersList.Day')} ${Number(calendarSettings.modalPeriods.fields.day) + 1})`"
                                        :value="calendarSettings.modalPeriods.fields.endTime"
                                        :state="calendarSettings.modalPeriods.states.endTime.valid"
                                        :feedback="
                                            $t(
                                                calendarSettings.modalPeriods.states.endTime
                                                    .feedback
                                            )
                                        "
                                        @input="handleOnInputEndTime"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="sidebar-modal-periods__footer-container">
                    <div
                        :class="[
                            'sidebar-modal-periods__footer-content-right',
                            calendarSettings.modalPeriods.periodId !== null ? '--is-edit' : ''
                        ]"
                    >
                        <div class="sidebar-modal-periods__footer-delete">
                            <sys-button
                                v-if="calendarSettings.modalPeriods.periodId !== null"
                                scheme="square"
                                variant="outline square-danger"
                                :size="handleFooterButtonSize()"
                                :full="windowWidth <= 480"
                                @click="handleDeletePeriod"
                            >
                                {{ $t("IntegrationPage.delete") }}
                            </sys-button>
                        </div>
                        <div class="sidebar-modal-periods__footer-buttons">
                            <sys-button
                                scheme="square"
                                variant="outline"
                                :size="handleFooterButtonSize()"
                                :full="windowWidth <= 480"
                                @click="handleCancelPeriod"
                            >
                                {{ $t("CalendarsPage.Cancel") }}
                            </sys-button>

                            <sys-button
                                scheme="square"
                                variant="contained"
                                :size="handleFooterButtonSize()"
                                :full="windowWidth <= 480"
                                @click="handleSavePeriod"
                            >
                                {{ $t("CalendarsPage.Save") }}
                            </sys-button>
                        </div>
                    </div>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
    import { mapState, mapMutations } from "vuex";
    import { BSidebar } from "bootstrap-vue";
    import SysButton from "@/@core/components/sys-button";
    import SysFormInput from "@/@core/components/sys-form-input";
    import SysFormDatepicker from "@/@core/components/sys-form-datepicker";
    import SysFormSelect from "@/@core/components/sys-form-select";
    import { timeToMinutes } from "../../utils";
    import { calendarInitialState } from "@/store/calendar";
    import { checkCompletedPeriods } from "@/views/pages/calendar/actionCalendar/utils";
    import { saveCalendarService } from "@/views/pages/calendar/actionCalendar/services";

    export default {
        data() {
            return {
                isOpen: false
            };
        },
        components: {
            BSidebar,
            SysButton,
            SysFormInput,
            SysFormSelect,
            SysFormDatepicker,
            AddIcon: () => import("@core/assets/icons/add-icon.svg"),
            CalendarIcon: () => import("@/assets/images/icons/calendar-icon.svg"),
            ArrowDownIcon: () => import("@core/assets/icons/arrow-down.svg"),
            SaveIcon: () => import("@core/assets/icons/save-icon.svg"),
            CloseIcon: () => import("@core/assets/icons/close-icon.svg")
        },
        methods: {
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            ...mapMutations("calendar", [
                "UPDATE_MODAL_PERIODS_TOGGLE",
                "UPDATE_MODAL_PERIODS_DAY_FIELD",
                "UPDATE_MODAL_PERIODS_STATUS_FIELD",
                "UPDATE_MODAL_PERIODS_EFFICIENCY_FIELD",
                "UPDATE_MODAL_PERIODS_START_TIME_FIELD",
                "UPDATE_MODAL_PERIODS_END_TIME_FIELD",
                "RESET_MODAL_PERIODS",
                "UPDATE_MODAL_PERIODS_STATES",
                "UPDATE_PERIODS_PERIOD_LIST",
                "UPDATE_PERIODS_IS_COMPLETED_PERIODS",
                "UPDATE_MODAL_PERIODS_IS_SAME_DAY_FIELD",
                "UPDATE_CONTEXT_DAYS"
            ]),
            mergeShifts(list) {
                const grouped = {};

                list.forEach((item) => {
                    const key = item.originalId || item.id;
                    if (!grouped[key]) {
                        grouped[key] = [];
                    }
                    grouped[key].push(item);
                });

                const result = [];

                Object.values(grouped).forEach((group) => {
                    if (group.length === 1) {
                        result.push(group[0]);
                    } else {
                        const sorted = group.sort((a, b) => a.day - b.day);
                        const merged = {
                            ...sorted[0],
                            endTime: sorted[sorted.length - 1].endTime,
                            day: sorted[0].day
                        };
                        merged.originalStartTime = sorted[0].originalStartTime;
                        merged.originalEndTime = sorted[sorted.length - 1].originalEndTime;

                        result.push(merged);
                    }
                });

                return result;
            },
            hasTimeConflict(newItem, existingList) {
                const newStart = this.convertToMinutesFromISO(newItem.startTime);
                const newEnd = this.convertToMinutesFromISO(newItem.endTime);

                return existingList.some((item) => {
                    const existingStart = this.convertToMinutes(item.startTime);
                    const existingEnd = this.convertToMinutes(item.endTime);

                    const isSameTime = newStart === existingStart && newEnd === existingEnd;
                    const hasOverlap = newStart < existingEnd && existingStart < newEnd;

                    return hasOverlap && !isSameTime;
                });
            },

            convertToMinutesFromISO(isoStr) {
                const date = new Date(isoStr);
                return date.getHours() * 60 + date.getMinutes();
            },

            convertToMinutes(timeStr) {
                const [hours, minutes] = timeStr.split(":").map(Number);
                return hours * 60 + minutes;
            },
            // Função para validar se uma hora está dentro do intervalo
            isTimesInRanges(startTime, endTime, day, list, periodId) {
                const startTimeInMinutes = timeToMinutes(startTime);
                const endTimeInMinutes = timeToMinutes(endTime);

                for (const range of list) {
                    if (day !== range.day) continue;

                    const rangeStartTimeInMinutes = timeToMinutes(range.startTime);
                    const rangeEndTimeInMinutes = timeToMinutes(range.endTime);

                    if (periodId?.toString() && range.id == periodId) continue;

                    if (
                        (startTimeInMinutes >= rangeStartTimeInMinutes &&
                            startTimeInMinutes < rangeEndTimeInMinutes) ||
                        (endTimeInMinutes > rangeStartTimeInMinutes &&
                            endTimeInMinutes <= rangeEndTimeInMinutes)
                    ) {
                        return true;
                    }

                    if (
                        startTimeInMinutes <= rangeStartTimeInMinutes &&
                        endTimeInMinutes >= rangeEndTimeInMinutes
                    ) {
                        return true;
                    }

                    if (
                        startTimeInMinutes === rangeStartTimeInMinutes &&
                        endTimeInMinutes === rangeEndTimeInMinutes
                    ) {
                        return true;
                    }
                }

                return false;
            },
            closeModal() {
                this.UPDATE_MODAL_PERIODS_TOGGLE(false);
                this.RESET_MODAL_PERIODS();
            },
            formatEfficiency(value) {
                const formattedValue =
                    value === "." ? "" : value.replace(/[^0-9.]/g, "").replace(/^0(\d)/, "$1");

                return formattedValue;
            },
            handleBlurEfficiency() {
                let formattedValue = this.calendarSettings.modalPeriods.fields.efficiency.replace(
                    /[^0-9.]/g,
                    ""
                );

                if (formattedValue[formattedValue.length - 1] == ".") {
                    formattedValue = formattedValue.replace(".", "");
                }

                this.UPDATE_MODAL_PERIODS_EFFICIENCY_FIELD(
                    formattedValue ? formattedValue + "%" : ""
                );
            },
            handleInputEfficiency(value) {
                const formattedValue = this.formatEfficiency(value);
                this.UPDATE_MODAL_PERIODS_EFFICIENCY_FIELD(formattedValue);
            },
            handleKeyPressEfficiency(event) {
                const key = event.key;

                if (!/[0-9.]/.test(key)) {
                    event.preventDefault();
                }

                if (
                    key === "." &&
                    this.calendarSettings.modalPeriods.fields.efficiency.includes(".")
                ) {
                    event.preventDefault();
                }
            },
            handleSelectStatus(value) {
                const efficiencySelected = !value?.toString()
                    ? 0
                    : this.context.status.find((e) => e.id === value)?.efficiency;

                this.UPDATE_MODAL_PERIODS_EFFICIENCY_FIELD(String(efficiencySelected ?? 0) + "%");
                this.UPDATE_MODAL_PERIODS_STATUS_FIELD(value);
            },
            handleFooterButtonSize() {
                if (this.windowWidth > 768) {
                    return "md";
                }

                if (this.windowWidth > 480 && this.windowWidth <= 768) {
                    return "md";
                }

                if (this.windowWidth <= 480) {
                    return "xs";
                }
            },
            adjustTimeZone3(date) {
                const offset = date.getTimezoneOffset() * 60000;
                const localISOTime = new Date(date.getTime() - offset).toISOString().slice(0, -1);

                return localISOTime;
            },
            handleSavePeriod() {
                const handleEndTime = (endTime) => {
                    if (endTime === "00:00") {
                        return "23:59";
                    }

                    return !!endTime
                        ? this.adjustTimeZone3(endTime)?.split("T")?.[1]?.substring(0, 5)
                        : null;
                };

                const handleCreate = (periodListCopy, newPeriod) => {
                    periodListCopy.push(newPeriod);
                    this.UPDATE_PERIODS_PERIOD_LIST([...periodListCopy]);

                    const isCompletedPeriods = checkCompletedPeriods(
                        periodListCopy,
                        Number(this.calendarSettings.generalApplied.duration)
                    );

                    this.UPDATE_PERIODS_IS_COMPLETED_PERIODS(isCompletedPeriods);
                    this.closeModal();

                    saveCalendarService(this);
                };

                const handleEdit = async (periodListCopy, newPeriod, originalId = null) => {
                    const periodIndex = periodListCopy.findIndex((i) => {
                        if (originalId) {
                            return i.originalId === originalId && i.day === newPeriod.day;
                        }
                        return i.id == newPeriod.id;
                    });

                    if (periodIndex === -1) return;

                    periodListCopy[periodIndex] = { ...newPeriod };

                    this.UPDATE_PERIODS_PERIOD_LIST([...periodListCopy]);

                    const isCompletedPeriods = checkCompletedPeriods(
                        periodListCopy,
                        Number(this.calendarSettings.generalApplied.duration)
                    );

                    this.UPDATE_PERIODS_IS_COMPLETED_PERIODS(isCompletedPeriods);
                    this.closeModal();

                    saveCalendarService(this);
                };

                const resultValidation = this.validateFields();

                if (!resultValidation) return;

                const fields = { ...this.calendarSettings.modalPeriods.fields };
                const periodList = [...this.periods.periodList];
                const isSameDay = this.calendarSettings.modalPeriods.fields.isSameDay;
                const isNewPeriod = this.calendarSettings.modalPeriods.periodId === null;

                if (!isSameDay) {
                    const startDateISO = this.adjustTimeZone3(fields.startTime);
                    const endDateISO = this.adjustTimeZone3(fields.endTime);

                    const [startDate] = startDateISO.split("T");
                    const [endDate] = endDateISO.split("T");
                    const originalId = crypto.getRandomValues(new Uint8Array(4)).join("");

                    const firstPeriod = {
                        id: crypto.getRandomValues(new Uint8Array(4)).join(""),
                        day: fields.day,
                        shiftId: fields.shift,
                        efficiency: fields.shift ? Number(fields.efficiency.replace(/%/g, "")) : 0,
                        startTime: startDateISO.split("T")[1].substring(0, 5),
                        endTime: "23:59",
                        originalStartTime: startDateISO,
                        originalEndTime: `${startDate}T23:59:00.000`,
                        isSameDay: false,
                        originalId: isNewPeriod
                            ? originalId
                            : this.calendarSettings.modalPeriods.originalId
                    };

                    const secondPeriod = {
                        id: crypto.getRandomValues(new Uint8Array(4)).join(""),
                        day: fields.day + 1,
                        shiftId: fields.shift,
                        efficiency: fields.shift ? Number(fields.efficiency.replace(/%/g, "")) : 0,
                        startTime: "00:00",
                        endTime: endDateISO.split("T")[1].substring(0, 5),
                        originalStartTime: `${endDate}T00:00:00.000`,
                        originalEndTime: this.addOneDayToISO(endDateISO),
                        isSameDay: false,
                        originalId: isNewPeriod
                            ? originalId
                            : this.calendarSettings.modalPeriods.originalId
                    };

                    if (isNewPeriod) {
                        handleCreate(periodList, firstPeriod);
                        handleCreate(periodList, secondPeriod);
                        return;
                    }

                    handleEdit(
                        periodList,
                        firstPeriod,
                        this.calendarSettings.modalPeriods.originalId
                    );
                    handleEdit(
                        periodList,
                        secondPeriod,
                        this.calendarSettings.modalPeriods.originalId
                    );
                } else {
                    const newPeriod = {
                        id:
                            this.calendarSettings.modalPeriods.periodId ??
                            crypto.getRandomValues(new Uint8Array(4)).join(""),
                        day: fields.day,
                        shiftId: fields.shift,
                        efficiency: fields.shift ? Number(fields.efficiency.replace(/%/g, "")) : 0,
                        startTime: this.adjustTimeZone3(fields.startTime)
                            ?.split("T")?.[1]
                            ?.substring(0, 5),
                        endTime: handleEndTime(fields.endTime),
                        originalStartTime: this.adjustTimeZone3(fields.startTime),
                        originalEndTime: this.adjustTimeZone3(fields.endTime),
                        isSameDay: true
                    };

                    if (isNewPeriod) {
                        handleCreate(periodList, newPeriod);
                        return;
                    }

                    handleEdit(periodList, newPeriod);
                }
            },
            handleOnInputEndTime(value) {
                const formattedEndTime = value
                    ? this.adjustTimeZone3(value)?.split("T")?.[1]?.substring(0, 5)
                    : null;

                if (!value) return;

                let lastHour = new Date();
                lastHour.setHours(23, 59, 0, 0);

                const endTimeInMinutes = value ? timeToMinutes(formattedEndTime) : null;

                this.UPDATE_MODAL_PERIODS_END_TIME_FIELD(endTimeInMinutes == 0 ? lastHour : value);
            },
            validateFields() {
                const vm = this;
                const modalPeriods = { ...this.calendarSettings.modalPeriods };
                const fields = { ...modalPeriods.fields };
                const states = { ...calendarInitialState().calendarSettings.modalPeriods };
                const formattedStartTime = fields.startTime
                    ? this.adjustTimeZone3(fields.startTime)?.split("T")?.[1]?.substring(0, 5)
                    : null;
                const formattedEndTime = fields.endTime
                    ? this.adjustTimeZone3(fields.endTime)?.split("T")?.[1]?.substring(0, 5)
                    : null;

                const startTimeInMinutes = fields.startTime
                    ? timeToMinutes(formattedStartTime)
                    : null;
                const endTimeInMinutes = fields.endTime ? timeToMinutes(formattedEndTime) : null;

                const validations = {
                    day: () => {
                        if (!fields.day?.toString()) {
                            states.day = {
                                valid: false,
                                feedback: "RequiredField"
                            };

                            return false;
                        }

                        states.day = {
                            valid: true,
                            feedback: ""
                        };

                        return true;
                    },
                    status: () => {
                        if (!fields.status?.toString()) {
                            states.status = {
                                valid: false,
                                feedback: "RequiredField"
                            };

                            return false;
                        }

                        states.status = {
                            valid: true,
                            feedback: ""
                        };

                        return true;
                    },
                    efficiency: () => {
                        if (!fields.efficiency) {
                            states.efficiency = {
                                valid: false,
                                feedback: "RequiredField"
                            };

                            return false;
                        }

                        states.efficiency = {
                            valid: true,
                            feedback: ""
                        };

                        return true;
                    },
                    startTime: () => {
                        if (!fields.startTime) {
                            states.startTime = {
                                valid: false,
                                feedback: "RequiredField"
                            };

                            return false;
                        }

                        states.startTime = {
                            valid: true,
                            feedback: ""
                        };

                        return true;
                    },
                    endTime: () => {
                        if (!fields.endTime) {
                            states.endTime = {
                                valid: false,
                                feedback: "RequiredField"
                            };

                            return false;
                        }

                        if (fields.startTime && fields.endTime) {
                            if (endTimeInMinutes <= startTimeInMinutes) {
                                states.endTime = {
                                    valid: false,
                                    feedback: "CalendarsPage.InvalidTime"
                                };

                                return false;
                            }
                        }

                        states.endTime = {
                            valid: true,
                            feedback: ""
                        };

                        return true;
                    },
                    startTime_endTime() {
                        if (
                            fields.startTime &&
                            fields.endTime &&
                            states.startTime.valid &&
                            states.endTime.valid
                        ) {
                            let endTimeInvalid = false;
                            if (!vm.calendarSettings.modalPeriods.originalId) {
                                endTimeInvalid = vm.isTimesInRanges(
                                    formattedStartTime,
                                    formattedEndTime,
                                    fields.day,
                                    vm.periods.periodList,
                                    vm.calendarSettings.modalPeriods.periodId,
                                    vm.calendarSettings.modalPeriods.fields.isSameDay
                                );
                            } else {
                                const combinedList = vm.mergeShifts(vm.periods.periodList);
                                const listWithoutCurrentPeriod = combinedList.filter(
                                    (e) =>
                                        e.originalId !== vm.calendarSettings.modalPeriods.originalId
                                );

                                const newItem = {
                                    startTime: vm.calendarSettings.modalPeriods.fields.startTime,
                                    endTime: vm.calendarSettings.modalPeriods.fields.endTime
                                };

                                endTimeInvalid = vm.hasTimeConflict(
                                    newItem,
                                    listWithoutCurrentPeriod
                                );
                            }

                            if (endTimeInvalid) {
                                states.startTime = {
                                    valid: false,
                                    feedback: "CalendarsPage.ConflictBetweenPeriods"
                                };
                                states.endTime = {
                                    valid: false,
                                    feedback: ""
                                };

                                return false;
                            }

                            states.startTime = {
                                valid: true,
                                feedback: ""
                            };

                            states.endTime = {
                                valid: true,
                                feedback: ""
                            };

                            return true;
                        }

                        return true;
                    }
                };

                let isValid = true;

                for (const action of Object.values(validations)) {
                    const resultValidation = action?.();

                    if (isValid) {
                        isValid = resultValidation;
                    }
                }

                this.UPDATE_MODAL_PERIODS_STATES({ ...states });

                return isValid;
            },
            handleDeletePeriod() {
                const modalPeriods = this.calendarSettings.modalPeriods;

                let updatedPeriodList;

                if (!modalPeriods.originalId) {
                    updatedPeriodList = this.periods.periodList.filter(
                        (period) => period.id !== modalPeriods.periodId
                    );
                } else {
                    updatedPeriodList = this.periods.periodList.filter(
                        (period) => period.originalId !== modalPeriods.originalId
                    );
                }

                this.UPDATE_PERIODS_PERIOD_LIST([...updatedPeriodList]);

                this.closeModal();
            },
            handleCancelPeriod() {
                this.closeModal();
            }
        },
        computed: {
            ...mapState("calendar", [
                "isModalPeriodsOpen",
                "calendarSettings",
                "context",
                "periods"
            ]),
            ...mapState("app", ["windowWidth"])
        },
        watch: {
            isModalPeriodsOpen(v) {
                this.isOpen = v;
            },
            "calendarSettings.general.fields.duration"(v) {
                const duration = this.calendarSettings.general.fields.duration;
                this.UPDATE_CONTEXT_DAYS(
                    duration > 0
                        ? Array.from({ length: Number(duration) }, (_, i) => ({
                              id: i + 1,
                              text: String(i + 1)
                          }))
                        : []
                );
            }
        }
    };
</script>

<style lang="scss">
    .sidebar-modal-periods {
        .b-sidebar-header {
            display: contents;

            .header-sidebar {
                width: 100%;
                padding: 16px;

                .sidebar-title {
                    display: flex;
                    align-items: center;
                    color: #4c4541;
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 600;

                    @media (max-width: 480px) {
                        font-size: 16px;
                    }
                }

                .close-icon {
                    border: 1px solid #974900;
                    border-radius: 5px;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;

                    svg {
                        width: 12px;
                        height: 12px;
                        path {
                            fill: #974900;
                        }
                    }

                    &:hover {
                        background-color: #ffede2 !important;
                    }

                    &:active {
                        background-color: #ffdbc4 !important;
                    }
                }
            }
        }

        .b-sidebar {
            width: 407px;
            box-shadow: 0px 4px 24px 0px rgba(58, 46, 38, 0.1);
            transition: all 0.5s ease-out;
            height: 100% !important;
            .b-sidebar-body {
                padding: 0;

                &:has(.--no-events) {
                    overflow-y: hidden;
                }

                .col-md-12 {
                    padding: 0;
                }

                &::-webkit-scrollbar {
                    display: none !important;
                }
            }
        }

        .footer-sidebar {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;

            .btn-apply {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                box-shadow: none;
                width: 100%;
                font-size: 14px;
                font-weight: 600;
                line-height: 20px;
                letter-spacing: 0.175px;
                padding: 8px 0;
                border: 1px solid #974900 !important;
                background-color: #974900 !important;
                color: #ffff !important;

                svg {
                    width: 14px;
                    height: 14px;
                    fill: #fff;
                }

                &:hover {
                    box-shadow: none;
                }

                @media (max-width: 480px) {
                    font-size: 12px;
                    padding: 3px 0;
                }
            }

            .footer-periods {
                display: flex;
                justify-content: space-between;
                gap: 8px;
                width: 100%;

                .year-block {
                    display: flex;
                    align-items: center;
                    padding: 4px 10px;
                    border-radius: 5px;
                    border: 1px solid #974900;

                    .years-dropdown {
                        padding: 0 8px 0 6px;
                    }

                    .arrow-icon,
                    .calendar-icon {
                        width: 14px;
                        height: 14px;
                        fill: #974900;
                    }

                    &:hover {
                        background-color: #ffede2 !important;
                    }

                    &:active {
                        background-color: #ffdbc4 !important;
                    }

                    @media (max-width: 480px) {
                        font-size: 12px;
                        padding: 0 10px;

                        svg {
                            width: 12px;
                            height: 12px;
                        }
                    }
                }

                .btn-new-event {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    color: #fff;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 20px;
                    letter-spacing: 0.175px;
                    border: 1px solid #974900 !important;
                    background-color: #974900 !important;
                    padding: 8px 14px;

                    svg {
                        width: 14px;
                        height: 14px;
                        fill: #fff;
                    }

                    &:hover {
                        box-shadow: none;
                    }

                    @media (max-width: 480px) {
                        font-size: 12px;
                        padding: 3px 14px;

                        svg {
                            width: 12px;
                            height: 12px;
                        }
                    }
                }

                .dropdown-toggle {
                    padding: 0;
                    color: #974900;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 20px;
                    letter-spacing: 0.175px;
                }

                .custom-dropdown-menu {
                    max-height: 200px;
                    overflow-y: auto;

                    .selected-year {
                        background-color: #974900;

                        .dropdown-item {
                            color: #fff;
                        }

                        .dropdown-item:hover,
                        .dropdown-item:focus {
                            color: #fff;
                        }

                        .dropdown-item:active {
                            background: #974900;
                        }
                    }

                    .dropdown-item:hover,
                    .dropdown-item:focus {
                        color: #974900;
                    }

                    .dropdown-item:active {
                        background: #ffdbc4;
                    }
                }
            }

            &.--no-events {
                border: none;
                padding: 0;

                .footer-periods {
                    display: none;
                }
            }
        }

        .separator {
            width: 100%;
            height: 1px;
            background: #cfc4be;
        }

        .sidebar-modal-periods__fields {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 16px;

            .radio-group {
                padding: 16px;
                border-radius: 6px;
                border: 1px solid #cfc4be;
                background: #fafafa;

                &.--disabled {
                    border-color: #7e7570;
                    background: #eee;
                }

                &.first-block {
                    margin-bottom: 16px;
                }

                .sidebar-modal-periods__fields-row {
                    padding-top: 16px;
                }
            }

            .sidebar-modal-periods__fields-row {
                display: flex;
                align-items: flex-start;
                gap: 16px;
                width: 100%;
            }

            .sidebar-modal-periods__hour-fields {
                display: flex;
                align-items: flex-start;
                gap: 16px;
                width: 100%;
            }

            .sidebar-modal-periods__day-field {
                max-width: 100%;
                width: 100%;
            }

            .sidebar-modal-periods__status-field,
            .sidebar-modal-periods__efficiency-field {
                min-width: 179.5px;
                max-width: 100%;
            }

            .sidebar-modal-periods__all-day-field {
                margin-top: 39px;
                display: flex;
            }
        }

        .sidebar-modal-periods__footer-container {
            border-top: 1px solid #cfc4be;
            padding: 15.5px 16px;
            display: flex;
            .sidebar-modal-periods__footer-content-right {
                margin-left: auto;
                display: flex;
                gap: 8px;

                .sidebar-modal-periods__footer-buttons {
                    display: flex;
                    gap: 8px;
                }

                &.--is-edit {
                    justify-content: space-between;
                    width: 100%;
                    margin-left: 0;
                }
            }
        }
    }

    @media (max-width: 480px) {
        .b-sidebar {
            width: 100% !important;
        }

        .sidebar-modal-periods__fields {
            .sidebar-modal-periods__status-field,
            .sidebar-modal-periods__efficiency-field {
                width: 100%;
                min-width: initial !important;
                max-width: initial !important;
            }

            .sidebar-modal-periods__starts-field,
            .sidebar-modal-periods__ends-field {
                width: 100%;
                min-width: initial !important;
                max-width: initial !important;
            }

            .sidebar-modal-periods__all-day-field {
                margin-top: 0 !important;
            }
        }

        .sidebar-modal-periods__footer-container {
            border-top: 1px solid #cfc4be;
            padding: 15.5px 16px;
            display: flex;
            .sidebar-modal-periods__footer-content-right {
                width: 100%;
                margin-left: initial !important;
                display: flex;
                gap: 8px;

                &.--is-edit .sidebar-modal-periods__footer-delete {
                    width: 33%;
                    .sidebar-modal-periods__footer-buttons {
                        width: 66%;
                        justify-content: flex-end;
                    }
                }
                .sidebar-modal-periods__footer-buttons {
                    width: 100%;
                }
            }
        }
    }
</style>
